import { useImperativeHandle, useState, type Ref } from 'react';
import { campaignCopy, isStaleRowError, useCampaignCapabilitiesStore } from '@discovr/features';
import type { HoldReasonCode, RosterEntryFinalized } from '@discovr/types';
import { HoldPostDialog } from './HoldPostDialog';
import { ReleaseHoldDialog } from './ReleaseHoldDialog';
import { useMenuDialogFocus } from '../hooks/useMenuDialogFocus';
import { mainPost } from './ConfirmedTab.helpers';
import { holdBlockedReason, isPostHeld } from './ConfirmedTab.hold';

export interface HoldDialogsHandle {
  openHold: (row: RosterEntryFinalized, opener?: HTMLElement | null) => void;
  openRelease: (row: RosterEntryFinalized, opener?: HTMLElement | null) => void;
}

const nameOf = (row: RosterEntryFinalized): string => row.creator?.name ?? campaignCopy.creator.hold.dialog.fallbackName;

export function ConfirmedHoldDialogs({ ref }: { ref: Ref<HoldDialogsHandle> }) {
  const { holdPost, releasePost } = useCampaignCapabilitiesStore();
  const [holdRow, setHoldRow] = useState<RosterEntryFinalized | null>(null);
  const [releaseRow, setReleaseRow] = useState<RosterEntryFinalized | null>(null);
  const [busy, setBusy] = useState(false);
  const { rememberFocus, restoreFocus } = useMenuDialogFocus();

  useImperativeHandle(
    ref,
    () => ({
      openHold: (row, opener) => {
        if (!holdPost || holdBlockedReason(mainPost(row)) !== null) return;
        rememberFocus(opener);
        setHoldRow(row);
      },
      openRelease: (row, opener) => {
        if (!releasePost || !isPostHeld(mainPost(row))) return;
        rememberFocus(opener);
        setReleaseRow(row);
      },
    }),
    [holdPost, releasePost, rememberFocus],
  );

  const confirmHold = async (reasonCode: HoldReasonCode, note: string) => {
    const post = holdRow && mainPost(holdRow);
    if (!holdRow || !post || !holdPost) return;
    const submitted = holdRow;
    const close = () => setHoldRow((current) => (current === submitted ? null : current));
    setBusy(true);
    try {
      await holdPost({ entryId: holdRow.id, postId: post.id, reasonCode, ...(note ? { note } : {}) });
      close();
    } catch (err) {
      // Toasted by the mutation hook. A stale row has been refetched, so a retry on
      // this captured row would fail again; otherwise keep the input to retry.
      if (isStaleRowError(err)) close();
    } finally {
      setBusy(false);
    }
  };

  const confirmRelease = async (note: string) => {
    const post = releaseRow && mainPost(releaseRow);
    if (!releaseRow || !post || !releasePost) return;
    const submitted = releaseRow;
    const close = () => setReleaseRow((current) => (current === submitted ? null : current));
    setBusy(true);
    try {
      await releasePost({ entryId: releaseRow.id, postId: post.id, ...(note ? { note } : {}) });
      close();
    } catch (err) {
      // Same rule as hold: close on a stale row, keep the input otherwise.
      if (isStaleRowError(err)) close();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <HoldPostDialog
        open={holdRow !== null}
        onOpenChange={(open) => !open && setHoldRow(null)}
        creatorName={holdRow ? nameOf(holdRow) : ''}
        busy={busy}
        onConfirm={confirmHold}
        onCloseAutoFocus={restoreFocus}
      />
      <ReleaseHoldDialog
        open={releaseRow !== null}
        onOpenChange={(open) => !open && setReleaseRow(null)}
        creatorName={releaseRow ? nameOf(releaseRow) : ''}
        busy={busy}
        onConfirm={confirmRelease}
        onCloseAutoFocus={restoreFocus}
      />
    </>
  );
}
